import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Clock3,
  ExternalLink,
  Flame,
  Globe2,
  Loader2,
  MapPin,
  MoreHorizontal,
  Search,
  Shapes,
  Sparkles,
  Store,
  X,
} from "lucide-react";
import EmptyState from "../components/EmptyState";
import ErrorState from "../components/ErrorState";
import ProductDetailModal from "../components/ProductDetailModal";
import SaveButton from "../components/buttons/SaveButton";
import { useUserContext } from "../context/UserContext";
import { useTheme } from "../context/ThemeContext";
import {
  getAffiliateLink,
  getDiscoveryConfig,
  getDiscoveryConfigQueryKey,
  getHotDealsPage,
  getHotDealsPageQueryKey,
  getPersonalizedFeedPage,
  getPersonalizedFeedPageQueryKey,
  recordFeedEvents,
  type FeedInteractionType,
  type FeedItem,
  type HotDealsCategorySection,
} from "../services/ProductsService";
import { formatLastUpdated, getProductUrl } from "../components/ProductItem.utils";
import { isRetailerClientEnabled, ONLINE_ONLY_RETAILERS } from "../config/retailers";
import { getProductImageUrl } from "../utils/imageUtils";
import { buildRetailerLookupUrl, getRetailerLinkLabel } from "../utils/productUrls";
import { isFreeTier } from "../utils/userUtils";
import { hasProPlan } from "../utils/retailerStoreLimits";
import { resolveStoreDisplayLabel } from "../utils/storeDisplayLabel";
import {
  RETAILERS_WITH_STORES,
  storesLoader,
  type RetailerWithStores,
} from "../utils/storesLoader";
import type { StoreData } from "../hooks/useStores";

type DealView = "for_you" | "top" | "all";
type FulfillmentMode = "local" | "online";
type DealQuickFilter = "in_stock" | "fresh" | "deep";

const FOR_YOU_PAGE_SIZE = 60;
const TOP_PAGE_SIZE = 60;
const ALL_PAGE_SIZE = 48;
const MINIMUM_DISCOUNT = 60;
const VARIETY_WINDOW = 24;
const VARIETY_PER_RETAILER = 3;
const GRID_GAP = 16;
const TILE_TEXT_HEIGHT = 132;
const TOP_DEALS_MAX_WIDTH = 1180;
const HIDDEN_RETAILERS_STORAGE_KEY = "dealhawk_best_deals_hidden_retailers";

const ONLINE_RETAILERS = new Set<string>(ONLINE_ONLY_RETAILERS);
const RETAILERS_WITH_STORE_CATALOGS = new Set<string>(RETAILERS_WITH_STORES);
const isBestDealsRetailerVisible = (retailer: string) =>
  retailer.toLowerCase() !== "menards" && isRetailerClientEnabled(retailer);

type StoreCatalogMap = Record<string, StoreData>;

const RETAILER_LABELS: Record<string, string> = {
  amazon: "Amazon",
  bestbuy: "Best Buy",
  costco: "Costco",
  dollar_general: "Dollar General",
  home_depot: "Home Depot",
  lowes: "Lowe's",
  tractor_supply: "Tractor Supply",
  northern_tool: "Northern Tool",
  samsclub: "Sam's Club",
  ace_hardware: "Ace Hardware",
  kohls: "Kohl's",
  target: "Target",
  target_online: "Target",
  walmart: "Walmart",
  walmart_online: "Walmart",
  woot: "Woot",
  newegg: "Newegg",
  gamestop: "GameStop",
  bjs: "BJ's Wholesale",
  meijer: "Meijer",
  menards: "Menards",
  academy: "Academy Sports",
  walgreens: "Walgreens",
  zappos: "Zappos",
  jcpenney: "JCPenney",
  belk: "Belk",
  michaels: "Michaels",
  nike: "Nike",
  adidas: "Adidas",
  reebok: "Reebok",
  skechers: "Skechers",
  puma: "Puma",
  underarmour: "Under Armour",
  columbia: "Columbia",
  nordstromrack: "Nordstrom Rack",
  aldo: "Aldo",
  vitacost: "Vitacost",
  journeys: "Journeys",
  shopdisney: "shopDisney",
  ninjakitchen: "Ninja Kitchen",
  hottopic: "Hot Topic",
  officedepot: "Office Depot",
  barnesnoble: "Barnes & Noble",
  razer: "Razer",
  pacsun: "PacSun",
  bathbodyworks: "Bath & Body Works",
  staples: "Staples",
  petsmart: "PetSmart",
  ulta: "Ulta Beauty",
  forever21: "Forever 21",
  gap: "Gap",
  petco: "Petco",
  americaneagle: "American Eagle",
  aerie: "Aerie",
  carters: "Carter's",
  abercrombie: "Abercrombie & Fitch",
  hollister: "Hollister",
  oldnavy: "Old Navy",
  bananarepublic: "Banana Republic",
  stevemadden: "Steve Madden",
  athleta: "Athleta",
  gapfactory: "Gap Factory",
  bananarepublicfactory: "Banana Republic Factory",
  boxlunch: "BoxLunch",
  verabradley: "Vera Bradley",
  footlocker: "Foot Locker",
  champs: "Champs Sports",
};

const RETAILER_COLORS: Record<string, string> = {
  amazon: "#ff9900",
  bestbuy: "#0046be",
  costco: "#e31837",
  dollar_general: "#a37200",
  home_depot: "#f96302",
  lowes: "#004990",
  tractor_supply: "#b31b34",
  northern_tool: "#d71920",
  samsclub: "#0067a0",
  ace_hardware: "#a6192e",
  kohls: "#5e2b97",
  target: "#cc0000",
  target_online: "#cc0000",
  walmart: "#0071ce",
  walmart_online: "#0071ce",
  woot: "#7ac143",
};

const money = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value || 0);

const latestPrice = (deal: FeedItem) =>
  deal.currentPrice || deal.storePrice?.[deal.storePrice.length - 1] || 0;

const isOnlineDeal = (deal: FeedItem) =>
  deal.storeId === "online" || ONLINE_RETAILERS.has(deal.retailer);

const cleanStoreId = (storeId?: string) =>
  (storeId || "")
    .replace("-PENNY-ITEM", "")
    .replace("-CRAZY-DEALS", "")
    .replace(/-wh$/, "");

const dealLocationLabel = (deal: FeedItem, storeCatalogs: StoreCatalogMap) => {
  if (isOnlineDeal(deal)) return "Online deal";
  const storeId = cleanStoreId(deal.storeId);
  const resolved = resolveStoreDisplayLabel(
    deal.retailer,
    deal.storeId,
    storeCatalogs[deal.retailer],
  );
  if (!resolved) return storeId ? `#${storeId}` : "In-store deal";
  if (!storeId) return resolved;
  const normalizedStoreId = storeId.replace(/^0+/, "") || "0";
  const resolvedStoreId = resolved.match(/#0*(\d+)\b/)?.[1]?.replace(/^0+/, "") || "0";
  if (resolvedStoreId === normalizedStoreId) return resolved;
  return `${resolved} - #${storeId}`;
};

const retailerLabel = (retailer: string) =>
  RETAILER_LABELS[retailer] || retailer.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());

const retailerFamily = (retailer: string) => {
  if (retailer === "walmart_online") return "walmart";
  if (retailer === "target_online") return "target";
  return retailer;
};

const dealTimestamp = (deal: FeedItem) =>
  deal.lastUpdated || deal.lastUpdates?.[deal.lastUpdates.length - 1] || deal.firstInserted || 0;

const dealFeedItemId = (deal: FeedItem) =>
  deal.feedItemId || `${deal.retailer}|${deal.upc || deal.sku || deal.name}|${deal.storeId}`;

const recordDealInteraction = (
  deal: FeedItem,
  type: FeedInteractionType,
  details: { dwellMs?: number } = {},
) => {
  const eventId = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  void recordFeedEvents([{
    eventId,
    type,
    itemId: dealFeedItemId(deal),
    retailer: deal.retailer,
    category: deal.category,
    name: deal.name,
    price: latestPrice(deal),
    fulfillment: isOnlineDeal(deal) ? "online" : "in_store",
    occurredAt: Date.now(),
    surface: "web_for_you",
    ...details,
  }]).catch(() => {
    // Feed learning should never interrupt browsing.
  });
};

const readHiddenRetailers = () => {
  if (typeof window === "undefined") return new Set<string>();
  try {
    const saved = JSON.parse(window.localStorage.getItem(HIDDEN_RETAILERS_STORAGE_KEY) || "[]");
    return new Set(Array.isArray(saved) ? saved.filter((retailer): retailer is string => typeof retailer === "string") : []);
  } catch {
    return new Set<string>();
  }
};

// The raw feed is sorted purely by discount, which lets one retailer's clearance
// run fill the entire first screen. Cap each retailer across the opening window
// and push the rest back, so overall discount ranking survives past the fold.
const orderByRetailerVariety = (items: FeedItem[]) => {
  const used = new Map<string, number>();
  const front: FeedItem[] = [];
  const back: FeedItem[] = [];

  for (const deal of items) {
    const seen = used.get(deal.retailer) || 0;
    if (front.length < VARIETY_WINDOW && seen < VARIETY_PER_RETAILER) {
      used.set(deal.retailer, seen + 1);
      front.push(deal);
    } else {
      back.push(deal);
    }
  }

  return [...front, ...back];
};

const columnsForWidth = (width: number) => {
  if (width >= 1020) return 4;
  if (width >= 720) return 3;
  return 2;
};

type DealStats = {
  online: number;
  local: number;
  retailers: number;
  highest: number;
  /** Set when the (possibly filtered) result set covers exactly one retailer. */
  onlyRetailer: string;
};

const plural = (count: number, noun: string) =>
  `${count.toLocaleString()} ${noun}${count === 1 ? "" : "s"}`;

const AFFILIATE_RETAILERS = ["amazon", "target", "target_online", "walmart", "walmart_online"];

function useDealLink(deal: FeedItem, onOutbound?: () => void) {
  const [loading, setLoading] = useState(false);
  const productId = deal.upc || deal.sku || "";
  const url =
    getProductUrl({ url: deal.url, retailer: deal.retailer, storeId: deal.storeId }) ||
    buildRetailerLookupUrl(deal.retailer, productId);

  const open = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!url || loading) return;
    const tab = window.open("about:blank", "_blank");
    onOutbound?.();
    setLoading(true);
    try {
      const target = AFFILIATE_RETAILERS.includes(deal.retailer)
        ? await getAffiliateLink(productId, url, deal.retailer)
        : url;
      if (tab) tab.location.href = target;
      else window.location.href = target;
    } catch {
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } finally {
      setLoading(false);
    }
  };

  return { productId, url, loading, open };
}

export default function FeaturedDealsPage() {
  const { user } = useUserContext();
  const { isDark } = useTheme();
  const isFreeUser = isFreeTier(user);
  const hasAccess = user?.admin === true || hasProPlan(
    (user?.retailers || []).map((retailer) => retailer.toLowerCase()),
  );
  const [view, setView] = useState<DealView>("all");
  const [fulfillment, setFulfillment] = useState<FulfillmentMode>("local");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hiddenRetailers, setHiddenRetailers] = useState<Set<string>>(readHiddenRetailers);
  const [selectedDeal, setSelectedDeal] = useState<FeedItem | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [storeCatalogs, setStoreCatalogs] = useState<StoreCatalogMap>({});
  const [dismissedFeedItems, setDismissedFeedItems] = useState<Set<string>>(new Set());
  const [mutedFeedRetailers, setMutedFeedRetailers] = useState<Set<string>>(new Set());
  const discoveryQuery = useQuery({
    queryKey: getDiscoveryConfigQueryKey(),
    queryFn: getDiscoveryConfig,
    enabled: Boolean(user?.retailers?.length) && hasAccess,
    staleTime: 5 * 60 * 1000,
  });
  const forYouEnabled = discoveryQuery.data?.forYouEnabled === true;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchTerm(trimmed);
      if (trimmed) setView("all");
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const pageSize = view === "for_you" ? FOR_YOU_PAGE_SIZE : view === "top" ? TOP_PAGE_SIZE : ALL_PAGE_SIZE;
  const requestCategory = view === "for_you" ? "" : selectedCategory;
  const requestSearch = view === "all" ? searchTerm : "";
  const query = useInfiniteQuery({
    queryKey: view === "for_you"
      ? [...getPersonalizedFeedPageQueryKey(pageSize, fulfillment), `view-${view}`]
      : [...getHotDealsPageQueryKey(1, pageSize, "discount", requestCategory, requestSearch, fulfillment), `view-${view}`],
    queryFn: ({ pageParam }) => view === "for_you"
      ? getPersonalizedFeedPage(pageParam.page, pageSize, fulfillment)
      : getHotDealsPage(
          pageParam.page,
          pageSize,
          "discount",
          pageParam.snapshotId,
          requestCategory,
          requestSearch,
          fulfillment,
        ),
    initialPageParam: { page: 1, snapshotId: undefined as string | undefined },
    getNextPageParam: (lastPage) => lastPage.hasMore
      ? { page: lastPage.page + 1, snapshotId: lastPage.snapshotId }
      : undefined,
    enabled: Boolean(user?.retailers?.length) && hasAccess && (view !== "for_you" || forYouEnabled),
    staleTime: 3 * 60 * 1000,
  });
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;

  useEffect(() => {
    try {
      window.localStorage.setItem(HIDDEN_RETAILERS_STORAGE_KEY, JSON.stringify(Array.from(hiddenRetailers).sort()));
    } catch {
      // The filter still works for this session when local storage is unavailable.
    }
  }, [hiddenRetailers]);

  const deals = useMemo(() => {
    const seen = new Set<string>();
    return (query.data?.pages.flatMap((page) => page.items) || []).filter((deal) => {
      if (!isBestDealsRetailerVisible(deal.retailer)) return false;
      if (fulfillment === "online" ? !isOnlineDeal(deal) : isOnlineDeal(deal)) return false;
      if (view !== "for_you" && deal.discountPercent < MINIMUM_DISCOUNT) return false;
      const key = `${deal.retailer}:${deal.upc || deal.sku || deal.name}:${deal.storeId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [fulfillment, query.data?.pages, view]);

  const presentedDeals = useMemo(
    () => view === "for_you"
      ? deals.filter((deal) =>
          !dismissedFeedItems.has(dealFeedItemId(deal)) &&
          !mutedFeedRetailers.has(retailerFamily(deal.retailer)),
        )
      : deals,
    [deals, dismissedFeedItems, mutedFeedRetailers, view],
  );

  const retailerOptions = useMemo(() => {
    const snapshotCounts = query.data?.pages[0]?.summary.retailerCounts;
    if (snapshotCounts?.length) {
      return snapshotCounts.filter(({ retailer }) => isBestDealsRetailerVisible(retailer)).sort((a, b) =>
        retailerLabel(a.retailer).localeCompare(retailerLabel(b.retailer)),
      );
    }
    const counts = new Map<string, number>();
    for (const deal of deals) counts.set(deal.retailer, (counts.get(deal.retailer) || 0) + 1);
    return Array.from(counts.entries())
      .map(([retailer, count]) => ({ retailer, count }))
      .sort((a, b) => retailerLabel(a.retailer).localeCompare(retailerLabel(b.retailer)));
  }, [deals, query.data?.pages]);

  const stats = useMemo(() => {
    const snapshotSummary = query.data?.pages[0]?.summary;
    if (snapshotSummary && (snapshotSummary.online || snapshotSummary.local)) {
      const visibleRetailerCounts = (snapshotSummary.retailerCounts || []).filter(
        ({ retailer }) => isBestDealsRetailerVisible(retailer),
      );
      const hasRetailerBreakdown = (snapshotSummary.retailerCounts || []).length > 0;
      const pausedLocalCount = (snapshotSummary.retailerCounts || [])
        .filter(({ retailer }) => !isBestDealsRetailerVisible(retailer))
        .reduce((sum, { count }) => sum + count, 0);
      const named = visibleRetailerCounts.length === 1
        ? retailerLabel(visibleRetailerCounts[0].retailer)
        : "";
      return {
        online: snapshotSummary.online,
        local: Math.max(0, snapshotSummary.local - pausedLocalCount),
        retailers: hasRetailerBreakdown
          ? visibleRetailerCounts.length
          : snapshotSummary.retailers,
        highest: pausedLocalCount > 0
          ? deals.reduce((max, deal) => Math.max(max, deal.discountPercent), 0)
          : snapshotSummary.highestDiscount,
        onlyRetailer: named,
      };
    }
    const online = deals.filter(isOnlineDeal).length;
    const retailers = new Set(deals.map((deal) => retailerLabel(deal.retailer)));
    const highest = deals.reduce((max, deal) => Math.max(max, deal.discountPercent), 0);
    return {
      online,
      local: deals.length - online,
      retailers: retailers.size,
      highest,
      onlyRetailer: retailers.size === 1 ? [...retailers][0] : "",
    };
  }, [deals, query.data?.pages]);

  const categorySections = useMemo(
    () => (query.data?.pages[0]?.categorySections ?? [])
      .map((section) => {
        const visibleDeals = section.deals.filter((deal) => isBestDealsRetailerVisible(deal.retailer));
        return {
          ...section,
          count: Math.max(visibleDeals.length, section.count - (section.deals.length - visibleDeals.length)),
          deals: visibleDeals,
        };
      })
      .filter((section) => section.count > 0 && section.deals.length > 0),
    [query.data?.pages],
  );
  const visibleTotal = useMemo(() => {
    const firstPage = query.data?.pages[0];
    const pausedCount = (firstPage?.summary.retailerCounts || [])
      .filter(({ retailer }) => !isBestDealsRetailerVisible(retailer))
      .reduce((sum, { count }) => sum + count, 0);
    return Math.max(deals.length, (firstPage?.total || deals.length) - pausedCount);
  }, [deals.length, query.data?.pages]);
  // A category-filtered response only carries that one section, so the full
  // list is captured from an unfiltered response and held. The API already
  // orders sections by strength (count x average discount), which is the order
  // the shortlist chips rely on.
  const [knownCategories, setKnownCategories] = useState<HotDealsCategorySection[]>([]);
  useEffect(() => {
    if (!selectedCategory && categorySections.length) setKnownCategories(categorySections);
  }, [categorySections, selectedCategory]);
  useEffect(() => {
    setSelectedCategory("");
    setKnownCategories([]);
  }, [fulfillment]);
  const categoryOptions = useMemo(
    () => [...knownCategories].sort((a, b) => a.category.localeCompare(b.category)),
    [knownCategories],
  );
  const dealStoreRetailers = useMemo(() => {
    const presentedDeals = [
      ...deals,
      ...categorySections.flatMap((section) => section.deals),
    ];
    return [...new Set(
      presentedDeals
        .filter((deal) => !isOnlineDeal(deal) && RETAILERS_WITH_STORE_CATALOGS.has(deal.retailer))
        .map((deal) => deal.retailer),
    )].sort();
  }, [categorySections, deals]);

  useEffect(() => {
    const needed = dealStoreRetailers.filter((retailer) => !storeCatalogs[retailer]);
    needed.forEach((retailer) => {
      storesLoader
        .loadStores(retailer as RetailerWithStores)
        .then((stores) => {
          setStoreCatalogs((current) => current[retailer]
            ? current
            : { ...current, [retailer]: stores });
        })
        .catch(() => {
          // Keep the numeric store fallback if a catalog is temporarily unavailable.
        });
    });
  }, [dealStoreRetailers, storeCatalogs]);

  const openDetails = (deal: FeedItem) => {
    if (deal.retailer === "woot" || !(deal.upc || deal.sku)) return;
    if (view === "for_you") recordDealInteraction(deal, "open");
    setSelectedDeal(deal);
    requestAnimationFrame(() => setModalVisible(true));
  };

  const closeDetails = () => {
    setModalVisible(false);
    window.setTimeout(() => setSelectedDeal(null), 200);
  };

  const clearSearch = useCallback(() => {
    setSearchInput("");
    setSearchTerm("");
  }, []);

  const toggleRetailer = useCallback((retailer: string) => {
    setHiddenRetailers((current) => {
      const next = new Set(current);
      if (next.has(retailer)) next.delete(retailer);
      else next.add(retailer);
      return next;
    });
  }, []);

  const showAllRetailers = useCallback(() => setHiddenRetailers(new Set()), []);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const showLess = useCallback((deal: FeedItem) => {
    recordDealInteraction(deal, "show_less");
    setDismissedFeedItems((current) => new Set(current).add(dealFeedItemId(deal)));
  }, []);

  const hideRetailerFromFeed = useCallback((deal: FeedItem) => {
    recordDealInteraction(deal, "hide_retailer");
    setMutedFeedRetailers((current) => new Set(current).add(retailerFamily(deal.retailer)));
  }, []);

  if (!hasAccess) {
    return (
      <EmptyState
        title="Best Deals is a paid feature"
        message={isFreeUser
          ? "Upgrade to see every 60%+ markdown across your stores and the best online retailers in one feed."
          : "Upgrade to Pro to unlock curated and personalized discovery across all of your retailers."}
        actionButton={{ text: "View Plans", to: "/settings?tab=billing&upgradeSource=hot_deals_lock" }}
      />
    );
  }

  if (!user?.retailers?.length) {
    return (
      <EmptyState
        title="Connect your retailers"
        message="Add your retailers and stores in Settings to build your personalized Best Deals feed."
        actionButton={{ text: "Open Settings", to: "/settings" }}
      />
    );
  }

  if (query.isLoading && !requestSearch && !requestCategory) return <BestDealsSkeleton view={view} />;

  if (query.error) {
    return (
      <ErrorState
        title={view === "for_you" ? "Could not load For You" : "Could not load Best Deals"}
        message={query.error instanceof Error ? query.error.message : "Unknown error"}
        onRetry={query.refetch}
      />
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white dark:bg-dark-900">
      <div className="relative z-50 shrink-0 border-b border-slate-200/80 bg-white/95 backdrop-blur-xl dark:border-dark-700 dark:bg-dark-900/95">
        <div className="grid min-w-0 grid-cols-1 gap-2 px-3 py-2 sm:grid-cols-[224px_minmax(220px,1fr)] sm:items-center sm:px-5">
          <div className="contents">
            <div className="grid h-10 min-w-0 grid-cols-2 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-0.5 dark:border-dark-700 dark:bg-dark-800 sm:h-9 sm:w-[224px] sm:rounded-xl" aria-label="Deal fulfillment">
              {([
                { id: "local" as const, label: "In store", Icon: Store },
                { id: "online" as const, label: "Online", Icon: Globe2 },
              ]).map(({ id, label, Icon }) => {
                const active = fulfillment === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setFulfillment(id)}
                    aria-pressed={active}
                    className={`inline-flex min-w-0 items-center justify-center gap-1.5 rounded-[0.8rem] px-2 text-[11px] font-extrabold transition ${active ? "bg-[#5b0a78] text-white shadow-sm" : "text-slate-600 hover:text-slate-950 dark:text-dark-300 dark:hover:text-white"}`}
                  >
                    <Icon size={13} />
                    {label}
                  </button>
                );
              })}
            </div>
            <label className="relative min-w-0 sm:ml-auto sm:w-full sm:max-w-sm">
              <span className="sr-only">Search deals</span>
              <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-dark-400" />
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search deals, products, or stores"
                className="h-11 w-full rounded-2xl border border-slate-200 bg-white pl-8 pr-8 text-xs font-semibold text-slate-800 outline-none transition placeholder:font-medium placeholder:text-slate-400 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/15 dark:border-dark-700 dark:bg-dark-800 dark:text-dark-100 dark:placeholder:text-dark-400 dark:focus:border-violet-500 dark:focus:ring-violet-500/15 sm:h-9 sm:rounded-xl"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:text-dark-400 dark:hover:bg-dark-700 dark:hover:text-dark-100"
                  aria-label="Clear search"
                >
                  <X size={12} />
                </button>
              )}
            </label>
            {view === "all" && (
              <div className="grid min-w-0 grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-2 sm:col-span-2 sm:flex sm:shrink-0 sm:items-center sm:justify-end">
                <RetailerFilter
                  options={retailerOptions}
                  hiddenRetailers={hiddenRetailers}
                  onToggle={toggleRetailer}
                  onShowAll={showAllRetailers}
                />
                <CategoryFilter
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="relative z-0 min-h-0 flex-1 overflow-hidden overscroll-contain">
        {query.isLoading ? (
          <div className="flex items-center justify-center gap-2 pt-24 text-sm font-semibold text-slate-500 dark:text-dark-300">
            <Loader2 size={16} className="animate-spin text-primary-600 dark:text-violet-300" />
            Searching deals…
          </div>
        ) : presentedDeals.length === 0 ? (
          <div className="mx-auto max-w-lg px-4 pt-16">
            {view === "for_you" ? (
              <EmptyState
                title="Your feed is catching up"
                message="Browse, save, and open a few products and DealHawk will start tuning this feed."
              />
            ) : requestSearch || requestCategory ? (
              <div className="rounded-2xl border border-slate-900/[0.06] bg-white px-5 py-12 text-center shadow-sm dark:border-white/[0.07] dark:bg-dark-900">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-700 dark:bg-violet-500/15 dark:text-violet-300">
                  <Search size={20} />
                </div>
                <h2 className="mt-4 text-lg font-black text-slate-950 dark:text-white">
                  No deals match{requestSearch ? ` “${requestSearch}”` : " that filter"}
                </h2>
                <p className="mx-auto mt-1 max-w-md text-sm text-slate-500 dark:text-dark-300">
                  Try a shorter search, a product type, or a retailer name{requestCategory ? ", or clear the category filter" : ""}.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    clearSearch();
                    setSelectedCategory("");
                  }}
                  className="mt-5 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-extrabold text-white transition hover:bg-primary-700 dark:bg-violet-600 dark:hover:bg-violet-500"
                >
                  Clear search & filters
                </button>
              </div>
            ) : (
              <EmptyState
                title="No 60%+ deals right now"
                message={fulfillment === "local"
                  ? "We’re scanning your stores. New qualifying in-store markdowns will appear here automatically."
                  : "We’re scanning online retailers. New qualifying online markdowns will appear here automatically."}
              />
            )}
          </div>
        ) : view !== "all" ? (
          <TopDealsView
            deals={presentedDeals}
            stats={stats}
            onOpenDetails={openDetails}
            storeCatalogs={storeCatalogs}
            hasMore={Boolean(hasNextPage)}
            isFetchingMore={isFetchingNextPage}
            onLoadMore={loadMore}
            personalized={view === "for_you"}
            fulfillment={fulfillment}
            onImpression={(deal) => recordDealInteraction(deal, "impression", { dwellMs: 1200 })}
            onOutbound={(deal) => recordDealInteraction(deal, "outbound_click")}
            onSaved={(deal) => recordDealInteraction(deal, "save")}
            onShowLess={showLess}
            onHideRetailer={hideRetailerFromFeed}
            onViewRetailers={() => setView("all")}
          />
        ) : (
          <AllDealsView
            key={`${selectedCategory || "all-categories"}-${requestSearch}`}
            deals={deals}
            searchTerm={requestSearch}
            total={visibleTotal}
            hiddenRetailers={hiddenRetailers}
            hasMore={Boolean(hasNextPage)}
            isFetchingMore={isFetchingNextPage}
            onLoadMore={loadMore}
            onOpenDetails={openDetails}
            isDark={isDark}
            storeCatalogs={storeCatalogs}
            fulfillment={fulfillment}
          />
        )}
      </main>

      {selectedDeal &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-end justify-center"
            style={{
              backgroundColor: modalVisible ? "rgba(0, 0, 0, 0.55)" : "rgba(0, 0, 0, 0)",
              backdropFilter: modalVisible ? "blur(8px)" : "blur(0px)",
              transition: "background-color 250ms ease-out, backdrop-filter 250ms ease-out",
            }}
            onPointerDown={(event) => event.target === event.currentTarget && closeDetails()}
          >
            <div
              className={`h-[78vh] w-full overflow-hidden rounded-t-3xl bg-white shadow-2xl transition-transform duration-200 dark:bg-dark-900 ${
                modalVisible ? "translate-y-0" : "translate-y-full"
              }`}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <ProductDetailModal
                sku={selectedDeal.upc || selectedDeal.sku || ""}
                storeId={selectedDeal.storeId}
                retailer={selectedDeal.retailer}
                onClose={closeDetails}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function SkeletonBlock({ className = "", style }: { className?: string; style?: React.CSSProperties }) {
  return <div className={`rounded-lg bg-slate-200 dark:bg-zinc-800 ${className}`} style={style} />;
}

function BestDealsSkeleton({ view }: { view: DealView }) {
  return (
    <div
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white dark:bg-dark-900"
      role="status"
      aria-live="polite"
      aria-label="Loading Best Deals"
    >
      <span className="sr-only">Loading Best Deals</span>

      <div className="shrink-0 border-b border-slate-200/80 bg-white dark:border-dark-700 dark:bg-dark-900">
        <div className="flex min-w-0 flex-col px-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="grid h-12 grid-cols-3 gap-2 sm:flex sm:w-auto sm:gap-6">
            <SkeletonBlock className="my-2 h-8 w-full sm:w-24" />
            <SkeletonBlock className="my-2 h-8 w-full sm:w-28" />
            <SkeletonBlock className="my-2 h-8 w-full sm:w-32" />
          </div>
          {view === "all" && (
            <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-2 border-t border-slate-200/80 py-2 dark:border-dark-700 sm:flex sm:border-0">
              <SkeletonBlock className="h-9 w-full sm:w-36" />
              <SkeletonBlock className="h-9 w-full sm:w-44" />
            </div>
          )}
        </div>
      </div>

      <div
        className={`min-h-0 flex-1 overflow-hidden pb-24 pt-4 sm:pb-5 ${
          view !== "all" ? "px-3 sm:px-5" : "px-0"
        }`}
      >
        <div className="mx-auto w-full max-w-[1180px] animate-pulse">
          {view !== "all" ? <TopDealsSkeleton /> : <AllDealsSkeleton />}
        </div>
      </div>
    </div>
  );
}

function TopDealsSkeleton() {
  return (
    <div>
      <SkeletonBlock className="h-6 w-32" />
      <SkeletonBlock className="mt-2 h-3.5 w-72" />

      <div className="mt-4 flex gap-1.5">
        {[64, 96, 80, 108, 72].map((width) => (
          <SkeletonBlock key={width} className="h-7 rounded-full" style={{ width }} />
        ))}
      </div>

      <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
        {Array.from({ length: 18 }).map((_, index) => (
          <div key={index} className="overflow-hidden rounded-xl border border-slate-200 dark:border-white/10">
            <SkeletonBlock className="aspect-square w-full rounded-none" />
            <div className="p-2.5">
              <SkeletonBlock className="h-3 w-24" />
              <SkeletonBlock className="mt-1.5 h-3 w-full" />
              <SkeletonBlock className="mt-1.5 h-4 w-16" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AllDealsSkeleton() {
  return (
    <div>
      <div className="flex items-end justify-between gap-3 px-3 pb-3 sm:px-5">
        <div className="space-y-2">
          <SkeletonBlock className="h-6 w-32" />
          <SkeletonBlock className="h-3 w-48" />
        </div>
      </div>
      <div>
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="flex min-w-0 items-center gap-2.5 border-b border-slate-200 bg-white px-3 py-[9px] dark:border-dark-800 dark:bg-dark-900"
          >
            <SkeletonBlock className="h-[70px] w-[70px] shrink-0 rounded-xl" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex gap-1.5">
                <SkeletonBlock className="h-5 w-20" />
                <SkeletonBlock className="h-5 w-24" />
              </div>
              <SkeletonBlock className="h-3.5 w-[88%]" />
              <SkeletonBlock className="h-3.5 w-[64%]" />
              <div className="flex items-center gap-2 pt-0.5">
                <SkeletonBlock className="h-5 w-16" />
                <SkeletonBlock className="h-3 w-12" />
              </div>
            </div>
            <SkeletonBlock className="h-8 w-8 shrink-0 rounded-xl" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryFilter({
  options,
  value,
  onChange,
  variant = "control",
}: {
  options: HotDealsCategorySection[];
  value: string;
  onChange: (category: string) => void;
  /** "chip" matches the Top Deals chip row, where this is the "More" overflow. */
  variant?: "control" | "chip";
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectedLabel = value || "All categories";
  const categoryChoices = useMemo(
    () => [{ category: "", count: options.reduce((total, option) => total + option.count, 0) }, ...options],
    [options],
  );
  const normalizedSearch = search.trim().toLowerCase();
  const filteredChoices = useMemo(
    () => categoryChoices.filter((option) => (option.category || "All categories").toLowerCase().includes(normalizedSearch)),
    [categoryChoices, normalizedSearch],
  );

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        setSearch("");
      }
    };
    const focusFrame = window.requestAnimationFrame(() => searchRef.current?.focus());

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const selectCategory = (category: string) => {
    onChange(category);
    setOpen(false);
    setSearch("");
  };

  const isChip = variant === "chip";

  return (
    <div ref={rootRef} className={isChip ? "relative shrink-0" : "relative min-w-0 sm:w-52 sm:shrink-0"}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={isChip
          ? `inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-[12.5px] font-semibold outline-none transition focus-visible:ring-2 focus-visible:ring-primary-600 ${
              open
                ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-dark-300 dark:hover:bg-white/10 dark:hover:text-white"
            }`
          : `flex h-9 min-w-0 w-full items-center gap-1.5 rounded-xl border bg-white px-2.5 text-[11px] font-bold outline-none transition focus-visible:ring-2 focus-visible:ring-primary-600 dark:bg-dark-800 dark:text-dark-100 dark:focus-visible:ring-violet-400 sm:h-8 sm:rounded-lg sm:text-xs ${
              open
                ? "border-primary-600 text-slate-950 ring-1 ring-primary-600/20 dark:border-violet-500 dark:text-white"
                : "border-slate-200 text-slate-700 hover:border-primary-600 dark:border-dark-700 dark:hover:border-violet-500"
            }`}
        aria-label={`Filter deals by category: ${selectedLabel}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="best-deals-category-options"
      >
        {isChip ? (
          <>
            All {options.length.toLocaleString()}
            <ChevronDown size={12} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
          </>
        ) : (
          <>
            <Shapes size={13} className="shrink-0" />
            <span className="min-w-0 flex-1 truncate text-left">{selectedLabel}</span>
            <ChevronDown size={12} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
          </>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-[80] w-[min(18rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-950/15 dark:border-dark-700 dark:bg-dark-900 sm:top-10 sm:w-72">
          <div className="border-b border-slate-100 p-2 dark:border-dark-800">
            <div className="relative">
              <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-dark-400" />
              <input
                ref={searchRef}
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search categories"
                aria-label="Search categories"
                className="h-8 w-full rounded-lg border border-slate-200 bg-slate-50 pl-8 pr-8 text-xs font-semibold text-slate-800 outline-none placeholder:font-medium placeholder:text-slate-400 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/15 dark:border-dark-700 dark:bg-dark-800 dark:text-dark-100 dark:placeholder:text-dark-400 dark:focus:border-violet-500 dark:focus:ring-violet-500/15"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    searchRef.current?.focus();
                  }}
                  className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-200 hover:text-slate-700 dark:text-dark-400 dark:hover:bg-dark-700 dark:hover:text-dark-100"
                  aria-label="Clear category search"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          <div id="best-deals-category-options" role="listbox" aria-label="Deal categories" className="max-h-52 overflow-y-auto p-1.5">
            {filteredChoices.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs font-semibold text-slate-500 dark:text-dark-300">
                No categories found
              </div>
            ) : (
              filteredChoices.map((option) => {
                const selected = value === option.category;
                return (
                  <button
                    key={option.category || "all-categories"}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => selectCategory(option.category)}
                    className={`flex h-9 w-full items-center gap-2 rounded-lg px-2.5 text-left transition ${
                      selected
                        ? "bg-primary-50 text-primary-800 dark:bg-violet-500/15 dark:text-violet-200"
                        : "text-slate-700 hover:bg-slate-50 dark:text-dark-100 dark:hover:bg-dark-800"
                    }`}
                  >
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${selected ? "bg-primary-600 text-white dark:bg-violet-500" : "bg-slate-100 text-slate-500 dark:bg-dark-800 dark:text-dark-300"}`}>
                      {selected ? <Check size={12} strokeWidth={3} /> : <Shapes size={11} />}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs font-bold">{option.category || "All categories"}</span>
                    <span className="text-[10px] font-semibold tabular-nums text-slate-400 dark:text-dark-400">
                      {option.count.toLocaleString()}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RetailerFilter({
  options,
  hiddenRetailers,
  onToggle,
  onShowAll,
}: {
  options: { retailer: string; count: number }[];
  hiddenRetailers: Set<string>;
  onToggle: (retailer: string) => void;
  onShowAll: () => void;
}) {
  const selectedCount = options.filter(({ retailer }) => !hiddenRetailers.has(retailer)).length;
  const allSelected = selectedCount === options.length;

  return (
    <details className="group relative min-w-0">
      <summary className="flex h-9 min-w-0 cursor-pointer list-none items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2.5 text-[11px] font-bold text-slate-700 outline-none transition hover:border-primary-600 focus-visible:ring-2 focus-visible:ring-primary-600 dark:border-dark-700 dark:bg-dark-800 dark:text-dark-100 dark:hover:border-violet-500 sm:h-8 sm:rounded-lg sm:text-xs [&::-webkit-details-marker]:hidden">
        <Store size={13} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate sm:flex-none">{allSelected ? "All retailers" : `${selectedCount}/${options.length} retailers`}</span>
        <ChevronDown size={12} className="shrink-0 transition-transform group-open:rotate-180" />
      </summary>

      <div className="absolute left-0 top-11 z-40 w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-950/15 dark:border-dark-700 dark:bg-dark-900 sm:left-auto sm:right-0 sm:top-10 sm:w-64">
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2.5 dark:border-dark-800">
          <div>
            <div className="text-xs font-black text-slate-950 dark:text-white">Show retailers</div>
            <div className="text-[10px] text-slate-500 dark:text-dark-300">Your choices are saved automatically.</div>
          </div>
          <button
            type="button"
            onClick={onShowAll}
            disabled={allSelected}
            className="text-[10px] font-black uppercase tracking-wide text-primary-600 disabled:opacity-35 dark:text-violet-300"
          >
            Show all
          </button>
        </div>

        <div className="max-h-72 overflow-y-auto p-1.5">
          {options.map(({ retailer, count }) => {
            const selected = !hiddenRetailers.has(retailer);
            return (
              <label
                key={retailer}
                className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-2 transition hover:bg-slate-50 dark:hover:bg-dark-800"
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggle(retailer)}
                  className="sr-only"
                />
                <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${selected ? "border-primary-600 bg-primary-600 text-white dark:border-violet-500 dark:bg-violet-500" : "border-slate-300 bg-white dark:border-dark-600 dark:bg-dark-800"}`}>
                  {selected && <Check size={11} strokeWidth={3} />}
                </span>
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: RETAILER_COLORS[retailer] || "#64748b" }}
                />
                <span className="min-w-0 flex-1 truncate text-xs font-bold text-slate-700 dark:text-dark-100">
                  {retailerLabel(retailer)}
                </span>
                <span className="text-[10px] font-semibold tabular-nums text-slate-400 dark:text-dark-400">{count}</span>
              </label>
            );
          })}
        </div>
      </div>
    </details>
  );
}

function TopDealsView({
  deals,
  stats,
  onOpenDetails,
  storeCatalogs,
  hasMore,
  isFetchingMore,
  onLoadMore,
  personalized,
  fulfillment,
  onImpression,
  onOutbound,
  onSaved,
  onShowLess,
  onHideRetailer,
  onViewRetailers,
}: {
  deals: FeedItem[];
  stats: DealStats;
  onOpenDetails: (deal: FeedItem) => void;
  storeCatalogs: StoreCatalogMap;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  personalized: boolean;
  fulfillment: FulfillmentMode;
  onImpression: (deal: FeedItem) => void;
  onOutbound: (deal: FeedItem) => void;
  onSaved: (deal: FeedItem) => void;
  onShowLess: (deal: FeedItem) => void;
  onHideRetailer: (deal: FeedItem) => void;
  onViewRetailers: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [recentCutoff] = useState(() => Date.now() - 24 * 60 * 60 * 1000);
  const [quickFilters, setQuickFilters] = useState<Set<DealQuickFilter>>(
    () => new Set<DealQuickFilter>(["in_stock"]),
  );

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    setViewportWidth(element.clientWidth);
    const observer = new ResizeObserver((entries) => {
      setViewportWidth(entries[0].contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const toggleQuickFilter = useCallback((filter: DealQuickFilter) => {
    setQuickFilters((current) => {
      const next = new Set(current);
      if (next.has(filter)) next.delete(filter);
      else next.add(filter);
      return next;
    });
  }, []);

  const filteredDeals = useMemo(() => {
    const ordered = orderByRetailerVariety(deals);
    if (personalized) return ordered;
    return ordered.filter((deal) => {
      if (quickFilters.has("in_stock") && deal.inStock === false) return false;
      if (quickFilters.has("fresh") && dealTimestamp(deal) < recentCutoff) return false;
      if (quickFilters.has("deep") && deal.discountPercent < 80) return false;
      return true;
    });
  }, [deals, personalized, quickFilters, recentCutoff]);

  const hero = personalized
    ? undefined
    : filteredDeals.reduce<FeedItem | undefined>(
        (best, deal) => (!best || deal.discountPercent > best.discountPercent ? deal : best),
        undefined,
      );
  const tiles = personalized ? filteredDeals : filteredDeals.filter((deal) => deal !== hero);

  const gutter = viewportWidth >= 640 ? 48 : 32;
  const contentWidth = Math.max(260, Math.min(TOP_DEALS_MAX_WIDTH, viewportWidth || TOP_DEALS_MAX_WIDTH) - gutter);
  const columns = columnsForWidth(contentWidth);
  const tileWidth = (contentWidth - (columns - 1) * GRID_GAP) / columns;
  const tileImageHeight = viewportWidth >= 640 ? tileWidth * 0.75 : tileWidth;
  const rowHeight = tileImageHeight + TILE_TEXT_HEIGHT + (personalized ? 20 : 0) + 20;
  const gridRowCount = Math.max(1, Math.ceil(tiles.length / columns));

  const virtualizer = useVirtualizer({
    count: gridRowCount + 1,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => (index === gridRowCount ? 72 : rowHeight),
    overscan: 4,
    getItemKey: (index) => (index === gridRowCount
      ? "top-deals-footer"
      : `top-deals-row-${index}-of-${columns}`),
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualIndex = virtualItems[virtualItems.length - 1]?.index ?? -1;

  useEffect(() => {
    if (lastVirtualIndex >= gridRowCount - 2 && hasMore && !isFetchingMore) onLoadMore();
  }, [gridRowCount, hasMore, isFetchingMore, lastVirtualIndex, onLoadMore]);

  const totalDeals = stats.online + stats.local;
  const summaryLine = personalized
    ? [
        `${plural(totalDeals, "deal")} picked from your activity`,
        `across ${plural(stats.retailers, "retailer")}`,
        fulfillment === "local" ? "in store" : "online",
      ].filter(Boolean).join(" · ")
    : [
        `${plural(totalDeals, "markdown")} at 60%+ off`,
        stats.onlyRetailer ? `from ${stats.onlyRetailer}` : `across ${plural(stats.retailers, "retailer")}`,
        stats.highest > 0 ? `up to ${stats.highest}% off` : "",
      ].filter(Boolean).join(" · ");

  if (!personalized) {
    return (
      <SelectedTopDealsView
        deals={filteredDeals}
        stats={stats}
        fulfillment={fulfillment}
        storeCatalogs={storeCatalogs}
        hasMore={hasMore}
        isFetchingMore={isFetchingMore}
        onLoadMore={onLoadMore}
        onOpenDetails={onOpenDetails}
        onOutbound={onOutbound}
        onSaved={onSaved}
        quickFilters={quickFilters}
        onToggleQuickFilter={toggleQuickFilter}
        onViewAll={onViewRetailers}
      />
    );
  }

  return (
    <div
      ref={scrollRef}
      data-products-scroll-container
      className="h-full w-full overflow-x-hidden overflow-y-auto overscroll-contain bg-white pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] dark:bg-dark-900 md:pb-0"
      role="region"
      aria-label={personalized ? "For You deals" : "Top deals"}
    >
      <div className="relative z-20 mx-auto w-full max-w-[1180px] px-4 pb-4 pt-5 sm:px-6 sm:pt-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-[22px] font-bold tracking-tight text-slate-950 dark:text-white sm:text-2xl">
              {personalized ? "Picked for you" : fulfillment === "local" ? "Top deals near you" : "Top online deals"}
            </h1>
            <p className="mt-1 text-[13px] text-slate-500 dark:text-dark-300">
              {personalized ? summaryLine : fulfillment === "local" ? "The strongest in-store markdowns updated in the last 24 hours." : "The strongest online markdowns updated in the last 24 hours."}
            </p>
          </div>
          {!personalized && (
            <button
              type="button"
              onClick={onViewRetailers}
              className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-3.5 text-xs font-extrabold text-slate-600 transition hover:border-violet-300 hover:text-violet-800 dark:border-white/10 dark:bg-dark-800 dark:text-dark-200 dark:hover:border-violet-500/50 dark:hover:text-violet-300 sm:self-auto"
            >
              <Store size={15} />
              {stats.retailers.toLocaleString()} retailers
              <ChevronDown size={13} className="-rotate-90" />
            </button>
          )}
        </div>
        {personalized ? (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1.5 text-[11px] font-bold text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
            <Sparkles size={12} />
            Learns from opens, saves, and retailer visits
          </div>
        ) : (
          <>
            <div className="mt-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {([
                { id: "in_stock" as const, label: "In stock", Icon: Check },
                { id: "fresh" as const, label: "Updated 24h", Icon: Clock3 },
                { id: "deep" as const, label: "80%+", Icon: Flame },
              ]).map(({ id, label, Icon }) => {
                const active = quickFilters.has(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleQuickFilter(id)}
                    aria-pressed={active}
                    className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-extrabold transition ${active ? "border-violet-300 bg-violet-50 text-violet-800 dark:border-violet-500/50 dark:bg-violet-500/15 dark:text-violet-200" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-white/10 dark:bg-dark-800 dark:text-dark-200"}`}
                  >
                    <Icon size={13} />
                    {label}
                  </button>
                );
              })}
            </div>

            {hero ? (
              <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
                <FeaturedDealHero
                  deal={hero}
                  storeCatalogs={storeCatalogs}
                  fulfillment={fulfillment}
                  onOpenDetails={onOpenDetails}
                  onOutbound={() => onOutbound(hero)}
                  onSaved={() => onSaved(hero)}
                />
                <aside className="hidden h-[208px] flex-col justify-between rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-dark-800 lg:flex" aria-label="Current Top Deals summary">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.14em] text-violet-700 dark:text-violet-300">Today’s feed</div>
                    <h2 className="mt-1 text-lg font-black tracking-tight text-slate-950 dark:text-white">Fresh markdowns, less noise.</h2>
                    <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-dark-300">Only 60%+ deals updated during the last 24 hours.</p>
                  </div>
                  <dl className="grid grid-cols-3 gap-2 border-t border-slate-200 pt-3 dark:border-white/10">
                    <div>
                      <dt className="text-[10px] font-bold text-slate-400 dark:text-dark-400">Deals</dt>
                      <dd className="mt-0.5 text-base font-black tabular-nums text-slate-900 dark:text-white">{totalDeals.toLocaleString()}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] font-bold text-slate-400 dark:text-dark-400">Retailers</dt>
                      <dd className="mt-0.5 text-base font-black tabular-nums text-slate-900 dark:text-white">{stats.retailers.toLocaleString()}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] font-bold text-slate-400 dark:text-dark-400">Best</dt>
                      <dd className="mt-0.5 text-base font-black tabular-nums text-emerald-700 dark:text-emerald-400">{stats.highest}%</dd>
                    </div>
                  </dl>
                </aside>
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-dashed border-slate-300 px-5 py-8 text-center dark:border-white/15">
                <p className="text-sm font-extrabold text-slate-800 dark:text-white">No matching deals</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-dark-300">Turn off a filter or switch fulfillment above.</p>
              </div>
            )}
          </>
        )}

        {tiles.length > 0 && (
          <div className="mt-6 flex items-end justify-between gap-4 border-b border-slate-200 pb-3 dark:border-white/10">
            <div className="min-w-0">
              <h2 className="text-base font-black tracking-tight text-slate-950 dark:text-white">
                {personalized ? "Recommended deals" : fulfillment === "local" ? "More top deals nearby" : "More top deals online"}
              </h2>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-dark-300">Sorted by discount with retailer variety mixed in.</p>
            </div>
            <span className="shrink-0 text-xs font-bold tabular-nums text-slate-400 dark:text-dark-400">
              {plural(tiles.length, "deal")}
            </span>
          </div>
        )}
      </div>

      <div
        className="relative mx-auto w-full max-w-[1180px]"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualItems.map((virtualItem) => {
          const isFooter = virtualItem.index === gridRowCount;
          const rowTiles = isFooter
            ? []
            : tiles.slice(virtualItem.index * columns, (virtualItem.index + 1) * columns);

          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              className="absolute left-0 top-0 w-full px-4 sm:px-6"
              style={{ transform: `translateY(${virtualItem.start}px)` }}
            >
              {isFooter ? (
                <div className="flex min-h-16 items-center justify-center pb-6 text-xs font-medium text-slate-400 dark:text-dark-400">
                  {isFetchingMore ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Loading more deals…
                    </span>
                  ) : hasMore ? (
                    "Scroll to load more deals"
                  ) : (
                    personalized ? "You’re all caught up for now." : "You’ve reached the end of the best deals."
                  )}
                </div>
              ) : (
                <div
                  className="grid pb-5"
                  style={{
                    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    columnGap: `${GRID_GAP}px`,
                  }}
                >
                  {rowTiles.map((deal, index) => (
                    <DealTile
                      key={`${deal.retailer}-${deal.upc || deal.sku || index}-${deal.storeId}`}
                      deal={deal}
                      onOpenDetails={onOpenDetails}
                      storeCatalogs={storeCatalogs}
                      personalized={personalized}
                      onImpression={onImpression}
                      onOutbound={onOutbound}
                      onSaved={onSaved}
                      onShowLess={onShowLess}
                      onHideRetailer={onHideRetailer}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SelectedTopDealsView({
  deals,
  stats,
  fulfillment,
  storeCatalogs,
  hasMore,
  isFetchingMore,
  onLoadMore,
  onOpenDetails,
  onOutbound,
  onSaved,
  quickFilters,
  onToggleQuickFilter,
  onViewAll,
}: {
  deals: FeedItem[];
  stats: DealStats;
  fulfillment: FulfillmentMode;
  storeCatalogs: StoreCatalogMap;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  onOpenDetails: (deal: FeedItem) => void;
  onOutbound: (deal: FeedItem) => void;
  onSaved: (deal: FeedItem) => void;
  quickFilters: Set<DealQuickFilter>;
  onToggleQuickFilter: (filter: DealQuickFilter) => void;
  onViewAll: () => void;
}) {
  const [featured, ...rest] = deals;
  const total = fulfillment === "local" ? stats.local : stats.online;

  return (
    <div
      data-products-scroll-container
      className="h-full w-full overflow-x-hidden overflow-y-auto overscroll-contain bg-white pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] dark:bg-dark-900 md:pb-8"
      role="region"
      aria-label="Top deals"
    >
      <div className="mx-auto w-full max-w-[960px]">
        <div className="flex gap-2 overflow-x-auto px-4 py-4 [scrollbar-width:none] sm:px-6 [&::-webkit-scrollbar]:hidden">
          {([
            { id: "in_stock" as const, label: "In stock", Icon: Check },
            { id: "fresh" as const, label: "Updated 24h", Icon: Clock3 },
            { id: "deep" as const, label: "80%+", Icon: Flame },
          ]).map(({ id, label, Icon }) => {
            const active = quickFilters.has(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => onToggleQuickFilter(id)}
                aria-pressed={active}
                className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-xs font-extrabold transition ${active ? "border-violet-300 bg-violet-50 text-violet-800 dark:border-violet-500/50 dark:bg-violet-500/15 dark:text-violet-200" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-white/10 dark:bg-dark-800 dark:text-dark-200"}`}
              >
                <Icon size={15} />
                {label}
              </button>
            );
          })}
        </div>

        {featured ? (
          <SelectedFeaturedDeal
            deal={featured}
            storeCatalogs={storeCatalogs}
            onOpenDetails={onOpenDetails}
            onOutbound={onOutbound}
            onSaved={onSaved}
          />
        ) : (
          <div className="mx-4 rounded-2xl border border-dashed border-slate-300 px-6 py-12 text-center dark:border-white/15 sm:mx-6">
            <p className="text-sm font-black text-slate-900 dark:text-white">No deals match these filters</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-dark-300">Turn off a filter or try the other fulfillment option.</p>
          </div>
        )}

        {rest.length > 0 && (
          <>
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-5 dark:border-white/10 sm:px-6">
              <div>
                <h2 className="text-lg font-black tracking-tight text-slate-950 dark:text-white">
                  {fulfillment === "local" ? "More top deals near you" : "More top deals online"}
                </h2>
                <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-dark-300">
                  {total.toLocaleString()} deals updated in the last 24 hours
                </p>
              </div>
              <button type="button" onClick={onViewAll} className="inline-flex items-center gap-1 text-sm font-black text-[#5b0a78] hover:text-violet-800 dark:text-violet-300">
                See all <ChevronRight size={17} />
              </button>
            </div>
            <div>
              {rest.map((deal, index) => (
                <SelectedDealRow
                  key={`${deal.retailer}-${deal.upc || deal.sku || index}-${deal.storeId}`}
                  deal={deal}
                  storeCatalogs={storeCatalogs}
                  onOpenDetails={onOpenDetails}
                  onOutbound={onOutbound}
                  onSaved={onSaved}
                />
              ))}
            </div>
          </>
        )}

        <div className="flex min-h-20 items-center justify-center px-4 py-5 text-xs font-semibold text-slate-400 dark:text-dark-400">
          {isFetchingMore ? (
            <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading more deals…</span>
          ) : hasMore ? (
            <button type="button" onClick={onLoadMore} className="rounded-xl border border-slate-200 px-5 py-2.5 font-extrabold text-slate-700 hover:border-violet-300 hover:text-violet-800 dark:border-white/10 dark:text-dark-100">Load more deals</button>
          ) : (
            "You’ve reached the end of today’s top deals."
          )}
        </div>
      </div>
    </div>
  );
}

function SelectedFeaturedDeal({
  deal,
  storeCatalogs,
  onOpenDetails,
  onOutbound,
  onSaved,
}: {
  deal: FeedItem;
  storeCatalogs: StoreCatalogMap;
  onOpenDetails: (deal: FeedItem) => void;
  onOutbound: (deal: FeedItem) => void;
  onSaved: (deal: FeedItem) => void;
}) {
  const { url, loading, open } = useDealLink(deal, () => onOutbound(deal));
  const price = latestPrice(deal);
  const place = isOnlineDeal(deal) ? "Online" : dealLocationLabel(deal, storeCatalogs);
  const detailsAvailable = deal.retailer !== "woot" && Boolean(deal.upc || deal.sku);

  return (
    <article
      onClick={() => detailsAvailable && onOpenDetails(deal)}
      className={`group mx-4 grid h-[226px] grid-cols-[130px_minmax(0,1fr)] overflow-hidden border-y border-slate-200 bg-white dark:border-white/10 dark:bg-dark-900 sm:mx-6 sm:h-[248px] sm:grid-cols-[230px_minmax(0,1fr)] sm:rounded-2xl sm:border ${detailsAvailable ? "cursor-pointer" : ""}`}
    >
      <div className="relative overflow-hidden bg-slate-50 dark:bg-dark-800">
        {deal.image ? (
          <img src={getProductImageUrl(deal.image, "400")} alt="" className="h-full w-full object-contain p-2 sm:p-4" />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-300"><Store size={36} /></div>
        )}
        <span className="absolute left-2 top-2 rounded-xl bg-rose-600 px-2 py-1.5 text-center text-xs font-black uppercase leading-4 text-white shadow-sm sm:text-sm">
          {deal.discountPercent}%<br className="sm:hidden" /> off
        </span>
      </div>
      <div className="relative flex min-w-0 flex-col justify-center p-4 pr-10 sm:p-7 sm:pr-16">
        <DealMeta deal={deal} storeLabel={place} isDark={false} />
        <h1 className="mt-3 line-clamp-3 text-xl font-black leading-tight tracking-tight text-slate-950 dark:text-white sm:text-3xl">{deal.name}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-3xl font-black tracking-tight text-emerald-700 dark:text-emerald-400 sm:text-4xl">{money(price)}</span>
          <span className="text-base text-slate-400 line-through dark:text-dark-400">{money(deal.msrp)}</span>
          {deal.inStock !== false && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-extrabold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"><Check size={14} /> In stock</span>
          )}
        </div>
        {url && (
          <button type="button" disabled={loading} onClick={open} className="mt-4 hidden w-fit items-center gap-2 rounded-xl bg-[#5b0a78] px-4 py-2.5 text-sm font-extrabold text-white hover:bg-violet-800 disabled:opacity-60 sm:inline-flex">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <ExternalLink size={15} />} View deal
          </button>
        )}
        <div onClick={(event) => event.stopPropagation()} className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full text-rose-500 sm:right-5 sm:top-5">
          <DealSaveButton deal={deal} className="!bg-transparent !p-2" onSaved={() => onSaved(deal)} />
        </div>
        <ChevronRight className="absolute bottom-4 right-4 text-slate-700 dark:text-dark-200 sm:bottom-7 sm:right-6" size={24} />
      </div>
    </article>
  );
}

function SelectedDealRow({
  deal,
  storeCatalogs,
  onOpenDetails,
  onOutbound,
  onSaved,
}: {
  deal: FeedItem;
  storeCatalogs: StoreCatalogMap;
  onOpenDetails: (deal: FeedItem) => void;
  onOutbound: (deal: FeedItem) => void;
  onSaved: (deal: FeedItem) => void;
}) {
  const { url, loading, open } = useDealLink(deal, () => onOutbound(deal));
  const price = latestPrice(deal);
  const place = isOnlineDeal(deal) ? "Online" : dealLocationLabel(deal, storeCatalogs);
  const detailsAvailable = deal.retailer !== "woot" && Boolean(deal.upc || deal.sku);

  return (
    <article
      onClick={() => detailsAvailable && onOpenDetails(deal)}
      className={`group grid min-h-[148px] grid-cols-[104px_minmax(0,1fr)] gap-3 border-b border-slate-200 bg-white px-4 py-4 transition hover:bg-slate-50 dark:border-white/10 dark:bg-dark-900 dark:hover:bg-dark-800 sm:min-h-[160px] sm:grid-cols-[126px_minmax(0,1fr)] sm:gap-5 sm:px-6 ${detailsAvailable ? "cursor-pointer" : ""}`}
    >
      <div className="relative overflow-hidden rounded-xl bg-slate-50 dark:bg-dark-800">
        {deal.image ? <img src={getProductImageUrl(deal.image, "400")} alt="" loading="lazy" className="h-full w-full object-contain p-2" /> : <div className="flex h-full items-center justify-center text-slate-300"><Store size={26} /></div>}
      </div>
      <div className="relative min-w-0 pr-10 sm:pr-16">
        <DealMeta deal={deal} storeLabel={place} isDark={false} />
        <h3 className="mt-2 line-clamp-2 text-[15px] font-black leading-5 text-slate-950 dark:text-white sm:text-lg sm:leading-6">{deal.name}</h3>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="rounded-lg bg-rose-600 px-2 py-1 text-xs font-black text-white">{deal.discountPercent}%</span>
          <span className="text-xl font-black text-emerald-700 dark:text-emerald-400 sm:text-2xl">{money(price)}</span>
          <span className="text-sm text-slate-400 line-through dark:text-dark-400">{money(deal.msrp)}</span>
          {deal.inStock !== false && <span className="ml-auto hidden items-center gap-1 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-extrabold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300 sm:inline-flex"><Check size={14} /> In stock</span>}
        </div>
        <div onClick={(event) => event.stopPropagation()} className="absolute right-0 top-0 flex items-center gap-1">
          {url && <button type="button" onClick={open} disabled={loading} aria-label={`Open ${deal.name}`} className="hidden h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-violet-800 dark:hover:bg-white/10 sm:flex">{loading ? <Loader2 size={16} className="animate-spin" /> : <ExternalLink size={16} />}</button>}
          <DealSaveButton deal={deal} className="!bg-transparent !p-2 !text-rose-500" onSaved={() => onSaved(deal)} />
        </div>
        <ChevronRight className="absolute bottom-0 right-0 text-slate-700 dark:text-dark-200" size={21} />
      </div>
    </article>
  );
}

function FeaturedDealHero({
  deal,
  storeCatalogs,
  fulfillment,
  onOpenDetails,
  onOutbound,
  onSaved,
}: {
  deal: FeedItem;
  storeCatalogs: StoreCatalogMap;
  fulfillment: FulfillmentMode;
  onOpenDetails: (deal: FeedItem) => void;
  onOutbound: () => void;
  onSaved: () => void;
}) {
  const { url, loading, open } = useDealLink(deal, onOutbound);
  const price = latestPrice(deal);
  const place = isOnlineDeal(deal) ? "Online" : dealLocationLabel(deal, storeCatalogs);
  const detailsAvailable = deal.retailer !== "woot" && Boolean(deal.upc || deal.sku);

  return (
    <article
      onClick={() => detailsAvailable && onOpenDetails(deal)}
      className={`grid overflow-hidden rounded-2xl bg-gradient-to-br from-[#3a075c] to-[#5b0a78] text-white shadow-sm ring-1 ring-[#3a075c]/10 transition-shadow hover:shadow-md sm:h-[208px] sm:grid-cols-[184px_minmax(0,1fr)] lg:grid-cols-[208px_minmax(0,1fr)] ${detailsAvailable ? "cursor-pointer" : ""}`}
      aria-label={`${fulfillment === "local" ? "Best markdown near you" : "Best online markdown"}: ${deal.name}`}
    >
      <div className="relative min-h-44 overflow-hidden bg-white sm:h-[208px] sm:min-h-0">
        {deal.image ? (
          <img src={getProductImageUrl(deal.image, "400")} alt="" className="h-full w-full object-contain" />
        ) : (
          <div className="flex h-full min-h-40 items-center justify-center text-slate-300"><Store size={34} /></div>
        )}
      </div>
      <div className="relative flex min-w-0 flex-col justify-center p-5 sm:px-6 sm:py-4">
        <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.16em] text-orange-300">
          <Flame size={13} />
          {fulfillment === "local" ? "Best markdown near you" : "Best online markdown"}
        </div>
        <h2 className="mt-1.5 max-w-3xl line-clamp-2 text-xl font-black leading-tight tracking-tight sm:text-[22px]">{deal.name}</h2>
        <div className="mt-2.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-[28px] font-black tracking-tight">{money(price)}</span>
          <span className="text-sm text-white/65 line-through">{money(deal.msrp)}</span>
          <span className="rounded-lg bg-orange-600 px-2.5 py-1 text-xs font-black">{deal.discountPercent}% off</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-bold text-white/75">
          <span>{retailerLabel(deal.retailer)}</span>
          <span>·</span>
          <span>{place}</span>
          {deal.inStock !== false && <span className="text-emerald-300">· In stock</span>}
          <span className="text-emerald-300">· Save {money(deal.savingsAmount)}</span>
        </div>
        <div className="mt-3 flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
          {url && (
            <button
              type="button"
              onClick={open}
              disabled={loading}
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-white/14 px-4 text-sm font-extrabold text-white transition hover:bg-white/22 disabled:opacity-60"
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : <ExternalLink size={15} />}
              View deal
            </button>
          )}
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-rose-500 shadow-sm">
            <DealSaveButton deal={deal} className="!bg-transparent !p-2" onSaved={onSaved} />
          </div>
        </div>
      </div>
    </article>
  );
}

function DealMeta({ deal, storeLabel, isDark }: { deal: FeedItem; storeLabel: string; isDark: boolean }) {
  const online = isOnlineDeal(deal);
  const color = RETAILER_COLORS[deal.retailer] || (isDark ? "#94a3b8" : "#475569");
  return (
    <div className="flex min-w-0 items-center gap-1.5 text-[10.5px] font-bold leading-4">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="shrink-0 text-slate-700 dark:text-dark-100">{retailerLabel(deal.retailer)}</span>
      <span className="shrink-0 text-slate-300 dark:text-dark-500">·</span>
      <span className="inline-flex min-w-0 items-center gap-1 font-semibold text-slate-500 dark:text-dark-300">
        {online ? <Globe2 size={10} className="shrink-0" /> : <MapPin size={10} className="shrink-0" />}
        <span className="truncate">{online ? "Online" : storeLabel}</span>
      </span>
    </div>
  );
}

function DealSaveButton({
  deal,
  className,
  onSaved,
}: {
  deal: FeedItem;
  className?: string;
  onSaved?: () => void;
}) {
  const price = latestPrice(deal);
  const online = isOnlineDeal(deal);
  const productId = deal.upc || deal.sku || "";
  const latestSalesFloor = deal.salesFloor?.[deal.salesFloor.length - 1];
  if (!productId) return null;

  return (
    <SaveButton
      productId={productId}
      productName={deal.name}
      productImage={deal.image}
      productCategory={deal.category}
      productStoreId={deal.storeId}
      onlinePrice={online ? price : undefined}
      storePrice={!online ? price : undefined}
      stock={typeof latestSalesFloor === "number" ? latestSalesFloor : undefined}
      retailerOverride={deal.retailer}
      onFavoriteAdded={onSaved}
      compact
      className={className}
      walmartOnlineData={deal.retailer === "walmart_online" ? {
        msrp: deal.msrp || 0,
        currentPrice: price,
        currentPrices: deal.storePrice || [],
        url: deal.url || "",
        sku: deal.sku || deal.upc || "",
        lastUpdated: dealTimestamp(deal),
        lastUpdates: deal.lastUpdates || [],
        discountPercentage: deal.discountPercent,
      } : undefined}
    />
  );
}

/**
 * The single card treatment for Top Deals. The photo fills its frame edge to
 * edge — product shots already ship with white padding baked in, so adding a
 * tinted tile behind them just renders a visible rectangle inside a rectangle.
 */
function DealTile({
  deal,
  onOpenDetails,
  storeCatalogs,
  personalized,
  onImpression,
  onOutbound,
  onSaved,
  onShowLess,
  onHideRetailer,
}: {
  deal: FeedItem;
  onOpenDetails: (deal: FeedItem) => void;
  storeCatalogs: StoreCatalogMap;
  personalized: boolean;
  onImpression: (deal: FeedItem) => void;
  onOutbound: (deal: FeedItem) => void;
  onSaved: (deal: FeedItem) => void;
  onShowLess: (deal: FeedItem) => void;
  onHideRetailer: (deal: FeedItem) => void;
}) {
  const tileRef = useRef<HTMLElement>(null);
  const impressionSent = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { url, loading, open } = useDealLink(
    deal,
    personalized ? () => onOutbound(deal) : undefined,
  );
  const price = latestPrice(deal);
  const online = isOnlineDeal(deal);
  const place = online ? "Online" : dealLocationLabel(deal, storeCatalogs);
  const detailsAvailable = deal.retailer !== "woot" && Boolean(deal.upc || deal.sku);
  const updated = formatLastUpdated([dealTimestamp(deal)], { emptyLabel: "Recently" });
  const retailerColor = RETAILER_COLORS[deal.retailer] || "#64748b";

  useEffect(() => {
    if (!personalized || impressionSent.current || !tileRef.current) return;
    let timer: number | undefined;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries[0]?.isIntersecting && entries[0].intersectionRatio >= 0.5;
      if (!visible) {
        if (timer) window.clearTimeout(timer);
        timer = undefined;
        return;
      }
      if (!timer) {
        timer = window.setTimeout(() => {
          impressionSent.current = true;
          onImpression(deal);
          observer.disconnect();
        }, 1000);
      }
    }, { threshold: [0.5] });
    observer.observe(tileRef.current);
    return () => {
      if (timer) window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [deal, onImpression, personalized]);

  return (
    <article
      ref={tileRef}
      onClick={() => detailsAvailable && onOpenDetails(deal)}
      className={`group flex min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-violet-200 hover:shadow-md dark:border-white/10 dark:bg-dark-900 dark:hover:border-violet-500/30 ${
        detailsAvailable ? "cursor-pointer" : ""
      }`}
    >
      <div className="relative aspect-square overflow-hidden border-b border-slate-100 bg-white sm:aspect-[4/3] dark:border-white/10">
        {deal.image ? (
          <img
            src={getProductImageUrl(deal.image, "400")}
            alt=""
            loading="lazy"
            className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-slate-50 text-slate-300">
            <Store size={28} />
          </div>
        )}

        {!personalized && (
          <div className="absolute bottom-2 left-2 rounded-lg bg-rose-600 px-2.5 py-1.5 text-[11px] font-black uppercase tracking-wide text-white shadow-lg shadow-rose-600/20">
            {deal.discountPercent}% off
          </div>
        )}

        <div className="absolute right-1.5 top-1.5 flex items-center gap-1">
          {url && (
            <div
              onClick={(event) => event.stopPropagation()}
              className="transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
            >
              <ShopIconButton loading={loading} onClick={open} retailer={deal.retailer} />
            </div>
          )}
          <div onClick={(event) => event.stopPropagation()} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-rose-500 shadow-sm transition-transform group-hover:scale-105 dark:bg-dark-800/95">
            <DealSaveButton
              deal={deal}
              className="!bg-transparent !p-1.5"
              onSaved={personalized ? () => onSaved(deal) : undefined}
            />
          </div>
          {personalized && (
            <div className="relative" onClick={(event) => event.stopPropagation()}>
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/85 text-slate-600 shadow-sm backdrop-blur transition hover:bg-white hover:text-slate-950 dark:bg-dark-800/85 dark:text-dark-200 dark:hover:bg-dark-800 dark:hover:text-white"
                aria-label={`Tune recommendations for ${deal.name}`}
              >
                <MoreHorizontal size={14} />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-8 z-30 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-xl dark:border-dark-700 dark:bg-dark-800">
                  <button
                    type="button"
                    onClick={() => onShowLess(deal)}
                    className="w-full rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:text-dark-100 dark:hover:bg-dark-700"
                  >
                    Show less like this
                  </button>
                  <button
                    type="button"
                    onClick={() => onHideRetailer(deal)}
                    className="w-full rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:text-dark-100 dark:hover:bg-dark-700"
                  >
                    Hide {retailerLabel(deal.retailer)}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="min-w-0 p-3">
        <div className="flex min-w-0 items-center gap-1 text-[10.5px] font-bold leading-4">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: retailerColor }} />
          <span className="shrink-0 text-slate-700 dark:text-dark-100">{retailerLabel(deal.retailer)}</span>
          {online ? <Globe2 size={10} className="shrink-0 text-slate-400" /> : <MapPin size={10} className="shrink-0 text-slate-400" />}
          <span className="truncate text-slate-500 dark:text-dark-300">{place}</span>
          {deal.inStock !== false && <span className="shrink-0 text-emerald-600 dark:text-emerald-400">· In stock</span>}
        </div>
        <h3 className="mt-1.5 line-clamp-2 min-h-10 text-sm font-bold leading-5 text-slate-800 dark:text-dark-100">
          {deal.name}
        </h3>
        {personalized && deal.reason && (
          <div className="mt-1.5 flex min-w-0 items-center gap-1 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
            <Sparkles size={10} className="shrink-0" />
            <span className="truncate">{deal.reason}</span>
          </div>
        )}
        <div className="mt-1.5 flex min-w-0 flex-wrap items-baseline gap-x-2">
          <span className="text-[18px] font-black tracking-tight text-emerald-700 dark:text-emerald-400">
            {money(price)}
          </span>
          <span className="text-xs text-slate-400 line-through dark:text-dark-400">{money(deal.msrp)}</span>
        </div>
        <div className="mt-1 flex min-w-0 items-center justify-between gap-2 text-[10.5px] font-bold">
          <span className="truncate text-emerald-700 dark:text-emerald-400">Save {money(deal.savingsAmount)}</span>
          <span className="inline-flex shrink-0 items-center gap-1 text-slate-400 dark:text-dark-400"><Clock3 size={10} />{updated}</span>
        </div>
      </div>
    </article>
  );
}

function ShopIconButton({
  loading,
  onClick,
  retailer,
}: {
  loading: boolean;
  onClick: (event: React.MouseEvent) => void;
  retailer: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      aria-label={`Shop at ${retailerLabel(retailer)}`}
      title={`Shop at ${retailerLabel(retailer)}`}
      className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/85 text-slate-600 shadow-sm backdrop-blur transition hover:bg-white hover:text-slate-950 disabled:opacity-50 dark:bg-dark-800/85 dark:text-dark-200 dark:hover:bg-dark-800 dark:hover:text-white"
    >
      {loading ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
    </button>
  );
}

function AllDealsView({
  deals,
  total,
  searchTerm = "",
  hiddenRetailers,
  hasMore,
  isFetchingMore,
  onLoadMore,
  onOpenDetails,
  isDark,
  storeCatalogs,
  fulfillment,
}: {
  deals: FeedItem[];
  total: number;
  searchTerm?: string;
  hiddenRetailers: Set<string>;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  onOpenDetails: (deal: FeedItem) => void;
  isDark: boolean;
  storeCatalogs: StoreCatalogMap;
  fulfillment: FulfillmentMode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const visibleDeals = useMemo(
    () => deals.filter((deal) => !hiddenRetailers.has(deal.retailer)),
    [deals, hiddenRetailers],
  );
  const virtualizer = useVirtualizer({
    count: visibleDeals.length + 2,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => index === 0 ? 72 : index === visibleDeals.length + 1 ? 68 : 150,
    overscan: 8,
    getItemKey: (index) => {
      if (index === 0) return "all-deals-header";
      if (index === visibleDeals.length + 1) return "all-deals-footer";
      const deal = visibleDeals[index - 1];
      return `${deal.retailer}-${deal.upc || deal.sku || deal.name}-${deal.storeId}`;
    },
  });
  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualIndex = virtualItems[virtualItems.length - 1]?.index ?? -1;

  useEffect(() => {
    if (lastVirtualIndex >= Math.max(1, visibleDeals.length - 4) && hasMore && !isFetchingMore) {
      onLoadMore();
    }
  }, [hasMore, isFetchingMore, lastVirtualIndex, onLoadMore, visibleDeals.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [hiddenRetailers]);

  return (
    <div
      ref={scrollRef}
      data-products-scroll-container
      className="h-full w-full overflow-x-hidden overflow-y-auto overscroll-contain bg-white pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] dark:bg-dark-900 md:pb-0"
    >
      <div
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualItems.map((virtualItem) => {
          const dealIndex = virtualItem.index - 1;
          const deal = visibleDeals[dealIndex];
          const isHeader = virtualItem.index === 0;
          const isFooter = virtualItem.index === visibleDeals.length + 1;

          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              className="absolute left-0 top-0 w-full px-3 sm:px-5"
              style={{ transform: `translateY(${virtualItem.start}px)` }}
            >
              {isHeader ? (
                <div className="flex min-w-0 items-start justify-between gap-2 pb-2.5 pt-3 sm:items-end sm:pb-3 sm:pt-4">
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-black tracking-tight text-slate-950 dark:text-white sm:text-lg">
                      {searchTerm ? `Results for “${searchTerm}”` : "All deals"}
                    </h2>
                    <p className="truncate text-[11px] font-medium text-slate-500 dark:text-dark-300 sm:text-xs">
                      {total.toLocaleString()} deals at 60%+ off · {fulfillment === "local" ? "in store" : "online"}
                    </p>
                  </div>
                </div>
              ) : isFooter ? (
                <div className="flex min-h-16 items-center justify-center pb-4 text-xs font-semibold text-slate-500 dark:text-dark-300">
                  {isFetchingMore ? (
                    <span className="inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin text-primary-600 dark:text-violet-300" /> Loading more deals…</span>
                  ) : visibleDeals.length === 0 ? (
                    "No deals from the selected retailers."
                  ) : hasMore ? (
                    "Scroll to load more deals"
                  ) : (
                    "You’ve reached the end of the best deals."
                  )}
                </div>
              ) : deal ? (
                <DealListRow
                  key={`${deal.retailer}-${deal.upc || deal.sku}-${deal.storeId}`}
                  deal={deal}
                  onOpenDetails={onOpenDetails}
                  isDark={isDark}
                  storeCatalogs={storeCatalogs}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DealListRow({
  deal,
  onOpenDetails,
  isDark,
  storeCatalogs,
}: {
  deal: FeedItem;
  onOpenDetails: (deal: FeedItem) => void;
  isDark: boolean;
  storeCatalogs: StoreCatalogMap;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { productId, url: productUrl, loading: affiliateLoading, open: openExternal } = useDealLink(deal);
  const price = latestPrice(deal);
  const online = isOnlineDeal(deal);
  const storeLabel = dealLocationLabel(deal, storeCatalogs);
  const detailsAvailable = deal.retailer !== "woot" && Boolean(deal.upc || deal.sku);
  const updated = formatLastUpdated([dealTimestamp(deal)], { emptyLabel: "Recently" });
  const lastChanged = formatLastUpdated(
    [deal.lastUpdated || deal.firstInserted],
    { emptyLabel: "Recently" },
  );
  const firstSeen = formatLastUpdated([deal.firstInserted], { emptyLabel: "Recently" });
  const latestSalesFloor = deal.salesFloor?.[deal.salesFloor.length - 1];
  const latestBackroom = deal.backroom?.[deal.backroom.length - 1];
  const inventoryLabel = online
    ? (deal.inStock ? "In stock online" : "Check availability")
    : [
        typeof latestSalesFloor === "number" ? `${latestSalesFloor} floor` : "",
        typeof latestBackroom === "number" && latestBackroom > 0 ? `${latestBackroom} backroom` : "",
      ].filter(Boolean).join(" · ") || (deal.inStock ? "In stock" : "Check availability");
  const productCodeLabel = deal.retailer === "home_depot"
    ? "Item ID"
    : deal.retailer === "target" || deal.retailer === "target_online"
      ? "TCIN"
      : "SKU";

  return (
    <article className="group mb-2.5 min-w-0 overflow-hidden rounded-2xl border border-slate-200/90 bg-white transition-colors hover:border-slate-300 hover:bg-slate-50/50 dark:border-dark-700 dark:bg-dark-900 dark:hover:border-dark-600 dark:hover:bg-dark-800/30 sm:mb-3">
      <div className="grid min-w-0 grid-cols-[84px_minmax(0,1fr)_38px] items-stretch gap-2.5 px-3 py-3 text-left sm:grid-cols-[116px_minmax(0,1fr)_48px] sm:gap-4 sm:px-4 sm:py-4 lg:grid-cols-[128px_minmax(0,1fr)_52px]">
        <button
          type="button"
          disabled={!detailsAvailable}
          onClick={() => onOpenDetails(deal)}
          className="relative aspect-square w-full self-center overflow-hidden rounded-xl bg-slate-50 ring-1 ring-slate-900/[0.05] transition-colors group-hover:bg-white disabled:cursor-default dark:bg-dark-800 dark:ring-white/[0.07] dark:group-hover:bg-dark-800/70 sm:rounded-2xl"
          aria-label={detailsAvailable ? `Open details for ${deal.name}` : undefined}
        >
          {deal.image ? (
            <img
              src={getProductImageUrl(deal.image, "150")}
              alt=""
              loading="lazy"
              className="h-full w-full object-contain p-2 transition duration-300 group-hover:scale-[1.03] sm:p-2.5"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-slate-300"><Store size={28} /></div>
          )}
        </button>

        <div className="flex min-w-0 flex-col justify-center self-stretch py-0.5">
          <DealMeta deal={deal} storeLabel={storeLabel} isDark={isDark} />
          <div className="mt-1.5 flex min-w-0 items-start">
            <button
              type="button"
              disabled={!detailsAvailable}
              onClick={() => onOpenDetails(deal)}
              className="min-w-0 flex-1 text-left disabled:cursor-default"
            >
              <h3 className="line-clamp-2 text-[13px] font-extrabold leading-[18px] tracking-[-0.12px] text-[#2f0a49] dark:text-zinc-50 sm:text-[16px] sm:leading-[21px]">{deal.name}</h3>
            </button>
          </div>
          <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 sm:mt-2.5 sm:gap-x-2.5">
              <span className="inline-flex shrink-0 items-center rounded-lg bg-rose-600 px-1.5 py-1 text-[9px] font-black tracking-[0.01em] text-white sm:px-2.5 sm:py-1.5 sm:text-xs">
                {deal.discountPercent}% OFF
              </span>
              <span className="text-[18px] font-black tracking-[-0.03em] text-emerald-600 dark:text-emerald-400 sm:text-[24px]">{money(price)}</span>
              <span className="text-[10px] font-medium text-slate-400 line-through dark:text-dark-400 sm:text-[13px]">{money(deal.msrp)}</span>
          </div>
          <div className="mt-1.5 flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 sm:mt-2">
            <span className="truncate text-[10px] font-extrabold text-emerald-700 dark:text-emerald-400/90 sm:text-xs">Save {money(deal.savingsAmount)}</span>
            <span className="inline-flex shrink-0 items-center gap-1 text-[9px] font-semibold text-slate-400 dark:text-dark-400 sm:text-[11px]">
                <Clock3 size={12} className="shrink-0" /> Changed {updated}
              </span>
          </div>
        </div>

        <div className="flex min-h-full flex-col items-center justify-between border-l border-slate-200/80 py-0.5 pl-2 dark:border-dark-700 sm:pl-3">
          <DealSaveButton
            deal={deal}
            className="!bg-transparent !p-1.5 hover:!bg-rose-50 dark:hover:!bg-rose-950/40"
          />
          <button
            type="button"
            onClick={() => setIsExpanded((expanded) => !expanded)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? `Collapse details for ${deal.name}` : `Expand details for ${deal.name}`}
            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-1 dark:focus-visible:ring-violet-500 sm:h-9 sm:w-9 ${
              isExpanded
                ? "border-primary-600 bg-primary-600 text-white shadow-sm dark:border-violet-500 dark:bg-violet-500"
                : "border-slate-200 bg-white text-slate-500 hover:border-primary-300 hover:text-primary-700 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-200 dark:hover:border-violet-600 dark:hover:text-violet-300"
            }`}
          >
            <ChevronDown size={16} className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      <div
        className="grid transition-[grid-template-rows,opacity] duration-200 ease-out"
        style={{ gridTemplateRows: isExpanded ? "1fr" : "0fr", opacity: isExpanded ? 1 : 0 }}
        aria-hidden={!isExpanded}
      >
        <div className="overflow-hidden">
          <div className="border-t border-slate-200/80 bg-slate-50/80 px-3 py-3 dark:border-dark-700 dark:bg-dark-950/45">
            {deal.reason && (
              <p className="mb-2.5 text-[11px] font-semibold leading-4 text-slate-600 dark:text-dark-200">
                {deal.reason}
              </p>
            )}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-4">
              <DealDetail label="Category" value={deal.category || "Uncategorized"} />
              <DealDetail label="Availability" value={inventoryLabel} />
              <DealDetail label={online ? "Channel" : "Store"} value={storeLabel} />
              <DealDetail label={productCodeLabel} value={productId || "Not provided"} />
              <DealDetail label="First seen" value={firstSeen} />
              <DealDetail label="Last changed" value={lastChanged} />
              <DealDetail label="Last checked" value={updated} />
              <DealDetail label="Savings" value={`${money(deal.savingsAmount)} · ${deal.discountPercent}% off`} />
              {deal.aisle && <DealDetail label="Aisle" value={deal.aisle} />}
              {deal.sellerName && <DealDetail label="Seller" value={deal.sellerName} />}
              {(deal.storeCount || 0) > 1 && (
                <DealDetail label="Store coverage" value={`${deal.storeCount} stores`} />
              )}
            </dl>
            {productUrl && (
              <button
                type="button"
                onClick={openExternal}
                disabled={affiliateLoading}
                className="mt-3 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-xl border border-sky-200 bg-sky-100/80 px-2.5 text-xs font-semibold text-sky-700 transition-all hover:bg-sky-200/80 active:scale-[0.99] disabled:opacity-60 dark:border-sky-700/50 dark:bg-sky-900/40 dark:text-sky-300 dark:hover:bg-sky-900/60"
              >
                {affiliateLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                {getRetailerLinkLabel(deal.retailer)}
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function DealDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-[9px] font-black uppercase tracking-[0.08em] text-slate-400 dark:text-dark-400">
        {label}
      </dt>
      <dd className="mt-0.5 break-words text-[11px] font-semibold leading-4 text-slate-700 dark:text-dark-100">
        {value}
      </dd>
    </div>
  );
}
